/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { type Client } from '../../lib/api/resources.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';

const COLS = ['patient', 'age', 'diagnosis', 'sessions', 'progress', 'next', 'status'] as const;

const STATUS_TONE = { active: 'green', inactive: 'slate', discharged: 'slate' } as const;

function PatientRow({
  client,
  nextIso,
  onOpen,
}: {
  readonly client: Client;
  readonly nextIso: string | undefined;
  readonly onOpen: (client: Client) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const open = useCallback(() => {
    onOpen(client);
  }, [client, onOpen]);
  return (
    <tr className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60" onClick={open}>
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
            {initialsOf(client.fullName)}
          </span>
          <span className="font-medium text-slate-800 dark:text-white">{client.fullName}</span>
        </div>
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{client.age ?? '—'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {client.primaryDiagnosis ?? '—'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{client.sessionsCount}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{client.progressPct}%</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {nextIso !== undefined ? formatIst(nextIso, 'EEE, h:mm a') : '—'}
      </td>
      <td className="px-5 py-3">
        <Badge label={t(`status.${client.status}`)} tone={STATUS_TONE[client.status]} />
      </td>
    </tr>
  );
}

/** Therapist → My Patients table (Phase 6 / Standalone-2). Row click → profile modal. */
export function PatientTable({
  rows,
  nextByClient,
  onOpen,
}: {
  readonly rows: readonly Client[];
  readonly nextByClient: ReadonlyMap<string, string>;
  readonly onOpen: (client: Client) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          <tr>
            {COLS.map((c) => (
              <th key={c} className="px-5 py-3 font-semibold">
                {t(`therapist.clients.col.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((c) => (
            <PatientRow key={c.id} client={c} nextIso={nextByClient.get(c.id)} onOpen={onOpen} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
